import { locatedStop } from '../../../shared/routeRetention';
import { scanAsk } from '../logic/daily';
import { dueRow } from './planDue';
import type { AreaStatus, IsoDate, RouteSettings, RouteStop, Suggestion } from '../../../shared/types';
import { shell } from '../app';
import { ApiError, NetworkError } from '../data/api';
import { loadRoute, loadRouteSettings, saveRoute, unwatched } from '../data/corridor';
import { estimateArea, getAreas, getSettings, markPlace, planDay, scanArea } from '../data/daily';
import type { PlaceMark } from '../data/daily';
import {
  clockText,
  dayTitle,
  leftOutCount,
  nextWorkday,
  suggestionToRoute,
  totalsText,
  visitedText,
} from '../logic/daily';
import { SuggestionHistory, unavailableSuggestion } from '../logic/suggestionHistory';
import { todayLocal } from '../logic/dates';
import { btn, empty, linkBtn } from '../ui/components';
import { h, replaceChildren } from '../ui/dom';
import { PLAN_GAP_REASON_LABELS } from '../ui/labels';
import { pointsMap } from '../ui/pointsMap';
import { money, monthsAgo, renderAcceptedPlan, renderScanAsk } from './planPrompts';
import { planStopCard } from './planStopCard';
import type { View } from '../ui/router';

/** Plan tomorrow (D83): the suggested route for the next workday, looked over and accepted. Nothing
 *  becomes his route without Accept, and no area is looked up on Google without his yes (D82). */

const friendly = (err: unknown): string =>
  err instanceof NetworkError
    ? 'Planning needs a connection.'
    : err instanceof ApiError
      ? err.message
      : 'Something went wrong. Try again.';

export const routePlanView: View = async ({ root }) => {
  shell.setChrome(true);
  shell.setTitle('Plan tomorrow');
  const host = h('div', { class: 'page page--plan' });
  const noticeNode = h('p', { role: 'status', 'aria-live': 'polite' });
  root.appendChild(host);
  const map = pointsMap('Map of the planned stops');
  let disposed = false;
  let seq = 0;
  let exclude: string[] = [];
  let areaOverride: string | undefined;
  let includeLeadIds: string[] = [];
  const history = new SuggestionHistory();
  let lastAreas: AreaStatus[] = [];
  let busy = false;
  let notice = '';
  let removedName = '';
  let failedRequest: string | null = null;
  const requestKey = (ids: readonly string[]) => JSON.stringify([skipScan, areaOverride, [...includeLeadIds].sort(), [...ids].sort()]);
  let settings: RouteSettings | null = null;
  let day: IsoDate | null = null;
  /** "Plan anyway": plan from the areas already looked up, for the rest of this visit. */
  let skipScan = false;
  /** The day's accepted plan is shown until he asks to plan again. */
  let showAccepted = true;

  const showMessage = (text: string, retry = false) => {
    replaceChildren(
      host,
      empty(text),
      noticeNode,
      retry ? btn('Try again', { variant: 'subtle', onClick: () => void load() }) : null,
      linkBtn('Back to Route', '#/route', { variant: 'ghost' }),
    );
    noticeNode.textContent = '';
  };

  const load = async (proposed = exclude, removed = '', choicesChanged = false) => {
    if (busy || disposed) return;
    busy = true;
    const mine = ++seq;
    notice = 'Planning…';
    if (history.current) render(history.current.suggestion, lastAreas);
    else showMessage('Planning…');
    try {
      settings ??= (await loadRouteSettings()) ?? (await getSettings());
      day ??= nextWorkday(todayLocal(), settings.workdays);
      const accepted = showAccepted ? loadRoute(day) : null;
      if (disposed || mine !== seq) return;
      if (accepted?.planned) {
        busy = false;
        renderAcceptedPlan(host, map.el, day, accepted, stopCard, () => {
          showAccepted = false;
          void load();
        });
        drawStops(accepted.stops);
        return;
      }
      const [s, areas] = await Promise.all([
        settings.warm_priority ? planDay(day, proposed, {skipScan, area:areaOverride, includeLeadIds}) :
          skipScan ? planDay(day, proposed, { skipScan: true }) : planDay(day, proposed),
        getAreas().catch((): AreaStatus[] => []),
      ]);
      if (disposed || mine !== seq) return;
      busy = false;
      lastAreas = areas;
      if (history.commit(s, proposed, removed ? 'remove' : choicesChanged ? 'choices' : 'replace',
        settings.warm_priority ? {area:areaOverride, includeLeadIds, skipScan} : undefined)) {
        if (removed && s.stops.length === 0) failedRequest = requestKey(proposed);
        removedName = removed;
        exclude = proposed;
        notice = '';
        render(s, areas);
      } else {
        failedRequest = requestKey(proposed);
        notice = unavailableSuggestion(history.current!.suggestion, areas, proposed.length > 0);
        render(history.current!.suggestion, areas);
      }
    } catch (err) {
      if (!disposed && mine === seq) {
        if (choicesChanged && history.current) {
          areaOverride = history.current.choices?.area;
          includeLeadIds = history.current.choices?.includeLeadIds ?? [];
        }
        busy = false;
        notice = `${friendly(err)} Keeping your current suggestion.`;
        if (history.current) render(history.current.suggestion, lastAreas);
        else showMessage(friendly(err), true);
      }
    }
  };

  const replan = (ids: readonly string[], removed = '') => {
    const proposed = [...new Set([...exclude, ...ids])];
    if (removed) failedRequest = null;
    else if (failedRequest === requestKey(proposed)) {
      notice = 'No new suggestion for these choices. Mark a place, look up an area, or start over before trying again.';
      noticeNode.textContent = notice;
      return;
    }
    void load(proposed, removed);
  };

  const mark = async (stop: RouteStop, kind: PlaceMark) => {
    if (busy) return;
    busy = true;
    if (history.current) render(history.current.suggestion, lastAreas);
    try {
      await markPlace(stop.place_id, kind);
      history.invalidate();
      failedRequest = null;
      removedName = '';
      busy = false;
      void load();
    } catch (err) {
      busy = false;
      if (!disposed && history.current) render(history.current.suggestion, lastAreas);
      shell.toast(friendly(err), { kind: 'error' });
    }
  };

  const scan = async (area: string) => {
    failedRequest = null;
    showMessage(`Looking up ${area}…`);
    try {
      const quote = await estimateArea(area);
      if (disposed) return;
      if (quote.calls < 1) { showMessage(scanAsk(quote), true); return; }
      const confirm = btn('Confirm scan', { variant: 'primary', onClick: async () => {
        confirm.disabled = true;
        try {
          const result = await scanArea(area, quote);
          if (result.error) shell.toast(result.error, { kind: 'error', duration: 8000 });
          void load();
        } catch (err) { if (!disposed) showMessage(friendly(err), true); }
      } });
      confirm.disabled = !!quote.cooldown_until && Date.parse(quote.cooldown_until) > Date.now();
      replaceChildren(host, h('p', {}, scanAsk(quote)), btn('Not now', {variant:'ghost', onClick:()=>void load()}), confirm);
      return;
    } catch (err) {
      if (!disposed) showMessage(friendly(err), true);
    }
  };

  const accept = async (s: Suggestion, button: HTMLButtonElement) => {
    if (!settings || busy || disposed) return;
    busy = true;
    host.querySelectorAll<HTMLButtonElement | HTMLSelectElement>('button, select').forEach((b) => {
      b.disabled = true;
    });
    button.disabled = true; // before the save settles: a second tap must not accept over the first
    const home = { label: settings.home_label, lat: settings.home_lat, lng: settings.home_lng };
    const previous = loadRoute(s.day);
    try {
      await saveRoute(suggestionToRoute(s, home, new Date().toISOString()), { accepted: true });
    } catch (err) {
      busy = false;
      host.querySelectorAll<HTMLButtonElement | HTMLSelectElement>('button, select').forEach((b) => {
        b.disabled = false;
      });
      button.disabled = false;
      shell.toast(`Could not save the route: ${err instanceof Error ? err.message : String(err)}`, {
        kind: 'error',
      });
      return;
    }
    busy = false;
    host.querySelectorAll<HTMLButtonElement | HTMLSelectElement>('button, select').forEach((b) => {
      b.disabled = false;
    });
    button.disabled = true;
    button.textContent = 'Accepted';
    if (previous)
      shell.toast(`${dayTitle(s.day)}: route replaced — tap to undo`, {
        duration: 8000,
        onClick: () => unwatched(saveRoute(previous)),
      });
    else shell.toast(`Accepted — your route for ${dayTitle(s.day)}`, { kind: 'success' });
  };

  const cardActions = {
    onRemove: (stop: RouteStop) => replan([settings?.warm_priority && stop.lead_id ? `lead:${stop.lead_id}` : stop.place_id], stop.name),
    onMark: (stop: RouteStop, kind: PlaceMark) => void mark(stop, kind),
  };
  const stopCard = (stop: RouteStop & { late?: boolean }, i: number, editable = true) =>
    planStopCard(stop, i, editable ? cardActions : null);

  const drawStops = (stops: readonly RouteStop[]) => {
    const points = stops.filter(locatedStop).map((st, i) => ({ position: { lat: st.lat, lng: st.lng }, label: String(i + 1) }));
    map.el.hidden = points.length === 0;
    void map.draw(points).catch(() => {
      map.el.hidden = true;
    });
  };

  const areaChoice = (s: Suggestion, areas: readonly AreaStatus[]) => {
    const select = h('select', {'aria-label':'Planning area'},
      h('option', {value:''}, `Automatic (${s.area ?? 'nearest area'})`),
      areas.map(a => h('option', {value:a.name}, a.name)));
    select.value = areaOverride ?? '';
    select.disabled = busy;
    select.addEventListener('change', () => {
      if (busy) return;
      areaOverride = select.value || undefined;
      failedRequest = null;
      void load(exclude, '', true);
    });
    return h('label', {}, 'Planning area ', select);
  };

  const render = (s: Suggestion, areas: readonly AreaStatus[]) => {
    // the count only for an area already looked up: a new one has nothing to count yet
    const area = areas.find((a) => a.name === s.area && a.scanned_at);
    const out = area ? leftOutCount(area) : 0;
    const line = [dayTitle(s.day), s.area, area ? visitedText(area) : null, out ? `${out} left out` : null]
      .filter(Boolean)
      .join(' · ');
    const head = h(
      'div',
      { class: 'planhead' },
      h('div', { class: 'planhead__day' }, line),
      s.set_aside && (s.set_aside.non_merchant_places || s.set_aside.non_merchant_signals)
        ? h('p', {}, `Set aside as probably not merchants: ${s.set_aside.non_merchant_places} cached places across ${s.set_aside.areas.join(', ')}; ${s.set_aside.non_merchant_signals} signal records skipped (may refer to the same places).`)
        : null,
      h(
        'div',
        { class: 'stack-h' },
        linkBtn('Areas', '#/route/areas', { variant: 'ghost', size: 'sm' }),
        linkBtn('Settings', '#/route/settings', { variant: 'ghost', size: 'sm' }),
      ),
    );
    if (s.needs_scan)
      return renderScanAsk(
        host,
        head,
        s.needs_scan,
        () => {
          skipScan = true;
          void load();
        },
        scan,
      );
    const ordinary = s.stops.filter((st) => !st.meeting).map((st) => settings?.warm_priority && st.lead_id ? `lead:${st.lead_id}` : st.place_id);
    const acceptBtn = btn('Accept', { variant: 'primary' });
    acceptBtn.addEventListener('click', () => void accept(s, acceptBtn));
    replaceChildren(
      host,
      head,
      h('div', { class: 'planhead__totals mono' }, s.totals.warm_stops !== undefined
        ? `${s.totals.warm_stops} follow-ups · ${s.totals.cold_stops ?? 0} new doors · ${s.totals.drive_min} min driving` : totalsText(s)),
      (s.totals.warm_stops ?? 0) > 0 ? h('p', {}, `${s.totals.warm_stops} follow-ups reserved first; room for at most ${Math.max(0, (settings?.target_stops ?? 0) - (s.totals.warm_stops ?? 0))} new doors. Driving may increase.`) : null,
      settings?.warm_priority ? areaChoice(s, areas) : null,
      s.scan_offer ? h('p', {}, `Optional: look up new doors in ${s.scan_offer.area} (next click up to ${money(s.scan_offer.cost_usd)}). `,
        btn('Look up new doors', {variant:'ghost', onClick:() => void scan(s.scan_offer!.area)})) : null,
      noticeNode,
      s.ignored_include_lead_ids?.length ? h('p', {}, `${s.ignored_include_lead_ids.length} previously included follow-ups are no longer due and were not added.`) : null,
      s.refresh
        ? h(
            'p',
            { class: 'plan__quiet' },
            `${s.refresh.area} was looked up ${monthsAgo(area?.scanned_at)} — look again for new places (next click up to ${money(s.refresh.cost_usd)}) `,
            btn('Look again', {
              variant: 'ghost',
              size: 'sm',
              onClick: () => void scan(s.refresh?.area ?? ''),
            }),
          )
        : null,
      loadRoute(s.day)
        ? h('p', { class: 'plan__quiet' }, `You have a route for ${dayTitle(s.day)}; accepting replaces it.`)
        : null,
      map.el,
      s.stops.length === 0
        ? empty(
            removedName
              ? `Nothing else fits once ${removedName} is removed. Go back to the previous suggestion or start over.`
              : exclude.length
                ? 'No suggestion fits these choices. Start over or look up an area.'
                : 'No open places to plan for this day. Look up an area.',
          )
        : h(
            'div',
            { class: 'stops' },
            s.stops.flatMap((st, i) => [
              ...(s.gaps ?? [])
                .filter((g) => g.before_stop === i)
                .map((g) =>
                  h(
                    'div',
                    { class: 'plan__quiet' },
                    `${clockText(g.start)}–${clockText(g.end)} · ${g.minutes} min waiting · ${g.reasons.map((r) => PLAN_GAP_REASON_LABELS[r]).join('; ')}`,
                  ),
                ),
              stopCard(st, i),
            ]),
          ),
      s.also_due.length > 0
        ? h(
            'div',
            { class: 'plan__due' },
            h('div', { class: 'routehead__label mono' }, 'ALSO DUE'),
            s.also_due.map(d => dueRow(d, {
              include: id => { if (busy) return; includeLeadIds = [...new Set([...includeLeadIds, id])]; failedRequest = null; void load(exclude, '', true); },
              clear: ids => { if (busy) return; failedRequest = null; void load(exclude.filter(id => !ids.includes(id)), '', true); },
            })),
          )
        : null,
      h(
        'div',
        { class: 'planbar' },
        history.previous.length
          ? btn('Previous suggestion', {
              variant: 'ghost',
              onClick: () => {
                if (busy) return;
                const old = history.back();
                if (old) {
                  exclude = old.exclude;
                  areaOverride = old.choices?.area;
                  includeLeadIds = old.choices?.includeLeadIds ?? [];
                  skipScan = old.choices?.skipScan ?? skipScan;
                  notice = '';
                  failedRequest = null;
                  removedName = '';
                  render(old.suggestion, lastAreas);
                }
              },
            })
          : null,
        btn('Start over', {
          variant: 'ghost',
          onClick: () => { failedRequest = null; areaOverride = undefined; includeLeadIds = []; void load([], '', !!settings?.warm_priority); },
        }),
        btn(
          failedRequest === requestKey([...new Set([...exclude, ...ordinary])])
            ? 'No other suggestion' : 'Another suggestion',
          { variant: 'subtle', onClick: () => replan(ordinary) },
        ),
        s.stops.length > 0 ? acceptBtn : null,
      ),
    );
    if (busy)
      host.querySelectorAll<HTMLButtonElement | HTMLSelectElement>('button, select').forEach((b) => {
        b.disabled = true;
      });
    noticeNode.textContent = notice;
    drawStops(s.stops);
  };

  void load();
  return () => {
    disposed = true;
    map.clear();
    noticeNode.remove();
  };
};
